// Helpers de formato y máscaras de input (RUT, moneda CLP, fechas, teléfono).
// Regla del proyecto: siempre aplicar máscara/validación a las casillas.

/** Formatea CLP: 15000 -> "$15.000". */
export function formatCLP(value: number, simbolo = "$"): string {
  const n = Math.round(value || 0);
  return `${simbolo}${n.toLocaleString("es-CL")}`;
}

/** Número con separador de miles, sin símbolo: 15000 -> "15.000". */
export function formatMiles(value: number): string {
  return Math.round(value || 0).toLocaleString("es-CL");
}

/** Limpia un RUT dejando dígitos y K. */
export function cleanRut(rut: string): string {
  return (rut || "").replace(/[^0-9kK]/g, "").toUpperCase();
}

/** Máscara de RUT chileno: "203481234" -> "20.348.123-4". */
export function formatRut(rut: string): string {
  const clean = cleanRut(rut);
  if (clean.length < 2) return clean;
  const cuerpo = clean.slice(0, -1);
  const dv = clean.slice(-1);
  const conPuntos = cuerpo.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  return `${conPuntos}-${dv}`;
}

/** Calcula el dígito verificador de un RUT (cuerpo sin DV). */
function computeDv(cuerpo: string): string {
  let suma = 0;
  let mul = 2;
  for (let i = cuerpo.length - 1; i >= 0; i--) {
    suma += parseInt(cuerpo[i], 10) * mul;
    mul = mul === 7 ? 2 : mul + 1;
  }
  const res = 11 - (suma % 11);
  if (res === 11) return "0";
  if (res === 10) return "K";
  return String(res);
}

/** Valida un RUT chileno con su dígito verificador. */
export function isValidRut(rut: string): boolean {
  const clean = cleanRut(rut);
  if (clean.length < 2) return false;
  const cuerpo = clean.slice(0, -1);
  const dv = clean.slice(-1);
  if (!/^\d+$/.test(cuerpo)) return false;
  return computeDv(cuerpo) === dv;
}

/** Normaliza un RUT para almacenar/comparar: sin puntos, con guion. "20.348.123-4" -> "20348123-4". */
export function normalizeRut(rut: string): string {
  const clean = cleanRut(rut);
  if (clean.length < 2) return clean;
  return `${clean.slice(0, -1)}-${clean.slice(-1)}`;
}

/** Máscara de fecha dd/mm/aaaa mientras se escribe. */
export function maskFecha(value: string): string {
  const d = value.replace(/\D/g, "").slice(0, 8);
  const parts = [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean);
  return parts.join("/");
}

/** ISO (o Date) -> "12/07/2026". */
export function formatFecha(iso?: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("es-CL", { day: "2-digit", month: "2-digit", year: "numeric" });
}

/** dd/mm/aaaa -> ISO (yyyy-mm-dd) o null si inválida. */
export function fechaToISO(ddmmaaaa: string): string | null {
  const m = ddmmaaaa.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
  if (isNaN(d.getTime())) return null;
  return `${yyyy}-${mm}-${dd}`;
}

/** Máscara de teléfono chileno móvil: "+56 9 XXXX XXXX". */
export function maskTelefono(value: string): string {
  const d = value.replace(/\D/g, "").replace(/^56/, "").slice(0, 9);
  if (!d) return "";
  const p1 = d.slice(0, 1);
  const p2 = d.slice(1, 5);
  const p3 = d.slice(5, 9);
  return `+56 ${[p1, p2, p3].filter(Boolean).join(" ")}`;
}

/** Email en minúsculas y sin espacios. */
export function normalizeEmail(email: string): string {
  return (email || "").trim().toLowerCase();
}
